import React, { ChangeEvent, useEffect, useState } from 'react';
import { createFilter } from 'react-select';
import { FileLoadingModel, LanguageModel, UploadDocumentTypesModel } from 'services/EpdClient';
import LanguagesService from 'services/LanguagesService';
import UploadDocumentTypesService from 'services/UploadDocumentTypesService';
import {
  addEpdFile,
  useAddEpdAdditionalDocument,
  useRemoveEpdAdditionalDocument,
  useUpdateEpdAdditionalInformation,
} from 'services/api/mutations';
import { useEPDDefinitionInformation, useEpdAdditionalDocuments, useEpdAdditionalInformation } from 'services/api/queries';
import { DoubleFieldPanel, FieldPanelFullWidth } from 'styles/v2/Styles.styled';
import { Option } from 'types/types';

import CompilerSelectControlledComponent from '../epd-compiler-inputs/CompilerSelectControlledComponent';
import WizardHTMLEditor from '../epd-wizard-inputs/WizardHTMLEditor';
import FilesList from '../file-row/FilesList';
import FileUpload from '../file-upload/FileUpload';
import TextWithAnimatedEllipsis from '../file-upload/TextWithAnimatedEllipsis';
import { HelpBox } from '../help-boxes';
import { ButtonUploadIcon } from '../icons';
import usePcrV2, { usePcrV2Loading } from './hooks/usePcrV2';
import SkeletonAdditionalInformationTab from './skeletons/SkeletonAdditionalInformationTab';

type OptionWithCode = Option & { code?: string };

const AdditionalInformationTab: React.FunctionComponent<{
  epdId: string;
  epdVersionId: string;
  isReadOnly: boolean;
  validationState: any;
  fileLoading?: FileLoadingModel[];
}> = ({ epdId, epdVersionId, fileLoading, validationState }) => {
  const { errors } = validationState || {};
  const epdDefinition = useEPDDefinitionInformation(epdVersionId).data;
  const additionalInformation = useEpdAdditionalInformation(epdVersionId).data;
  const documents = useEpdAdditionalDocuments(epdVersionId).data;
  const isPcrV2Loading = usePcrV2Loading();
  const isPcrV2 = usePcrV2();

  const updateMutation = useUpdateEpdAdditionalInformation(epdVersionId);

  const onChangeEpdAdditionalInformation = async (propertyName: string, val: any) => {
    updateMutation.mutate({ epdId, versionId: epdVersionId, propertyName: propertyName, newValue: val });
  };

  const [documentTypeList, setDocumentTypes] = useState<UploadDocumentTypesModel[]>([]);
  const [languageList, setLanguages] = useState<LanguageModel[]>([]);

  const [documentType, setDocumentType] = useState<Option>();
  const [documentLanguage, setDocumentLanguage] = useState<OptionWithCode>();

  const addEPDDocumentMutation = useAddEpdAdditionalDocument(epdVersionId);
  const removeEPDDocumentMutation = useRemoveEpdAdditionalDocument(epdVersionId);

  const onRemoveEPDDocument = (fileId: string) => {
    removeEPDDocumentMutation.mutate(fileId);
  };

  useEffect(() => {
    const fetchDocumentType = async () => {
      const res = await UploadDocumentTypesService.getDocumentTypes(true);
      setDocumentTypes(res);
      return res;
    };
    const fetchLanguages = async () => {
      const result = await LanguagesService.getLanguages();
      setLanguages(result);
    };

    fetchLanguages();
    fetchDocumentType();
  }, []);

  const isLoading =
    epdDefinition === undefined || additionalInformation === undefined || documents === undefined || isPcrV2Loading;
  if (isLoading) {
    return <SkeletonAdditionalInformationTab />;
  }

  const filterConfig = {
    ignoreCase: true,
    ignoreAccents: true,
    trim: true,
    matchFrom: 'start' as const,
  };

  const uploadFile = async (e: ChangeEvent<HTMLInputElement>, propertyName: string) => {
    const target = e.target as HTMLInputElement;
    let file = target && target.files && target.files[0];

    if (!file || !epdId) {
      return null;
    }

    if (documentType?.value !== '2489c33d-56f6-4fe7-b5cd-a94e82900708') {
      propertyName = 'DocumentOther';
    }

    const documentTypeId = documentType?.value ?? null;
    const documentLanguageId = documentLanguage?.value ?? null;

    await addEpdFile({ epdId, propertyName, file, documentTypeId, documentLanguageId }, addEPDDocumentMutation);

    // @ts-ignore
    setDocumentType(null);
    // @ts-ignore
    setDocumentLanguage(null);
  };

  return (
    <>
      <FieldPanelFullWidth style={{ gridRowGap: 0 }}>
        <HelpBox>
          <span>
            Please upload EPD related documents in PDF format. Several documents can be uploaded.
            <br />
            Document type and language should be specified before uploading.
          </span>
        </HelpBox>
        <DoubleFieldPanel className="mt-5">
          <CompilerSelectControlledComponent
            label="Document type"
            tooltip="EPD related documents."
            name="docType"
            options={documentTypeList?.map((item) => {
              return {
                value: item.id,
                label: item.name,
              } as Option;
            })}
            value={documentType}
            onChanged={(name: string, value: string) => {
              if (!value) {
                setDocumentType(undefined);
                return;
              }
              setDocumentType({ value: value, label: documentTypeList.find((x) => x.id == value)?.name || value });
            }}
            isClearable={true}
          />
          <CompilerSelectControlledComponent
            label="Document language"
            tooltip="Specify the language of the provided document."
            name="docLang"
            options={languageList?.map((item) => {
              return {
                value: item.id,
                label: item.name,
                code: item.code,
              } as OptionWithCode;
            })}
            value={documentLanguage}
            onChanged={(name: string, value: string) => {
              setDocumentLanguage({
                value: value,
                label: languageList.find((x) => x.id == value)?.name || value,
                code: languageList.find((x) => x.id == value)?.code,
              });
            }}
            isClearable={false}
            filterOption={createFilter(filterConfig)}
          />
        </DoubleFieldPanel>
        <FileUpload
          label="Upload document"
          name="Document"
          icon={ButtonUploadIcon}
          accept=".pdf"
          validExtensions={['pdf']}
          onFileUpload={uploadFile}
          disabled={!(documentLanguage && documentType) || addEPDDocumentMutation.isPending}
        />
        {addEPDDocumentMutation.isPending && (
          <TextWithAnimatedEllipsis className="mt-2">Uploading file</TextWithAnimatedEllipsis>
        )}
        <FilesList
          files={documents}
          onRemoveFile={onRemoveEPDDocument}
          fileLoading={
            fileLoading?.find(
              (loading) =>
                (loading.fileType === 'Document' || loading.fileType === 'DocumentOther') && loading.loadingsCount > 0
            ) !== undefined
          }
        />
      </FieldPanelFullWidth>
      {additionalInformation !== undefined && (
        <FieldPanelFullWidth>
          <WizardHTMLEditor
            name="additional.environmental"
            value={additionalInformation?.environmental}
            label="Additional environmental information"
            tooltip="Declare additional environmentally relevant information not derived from the LCA. Please refer to the PCR 2019:14 for more details."
            tooltipPosition="right"
            onChanged={onChangeEpdAdditionalInformation}
            stripPastedStyles={true}
            error={errors?.environmental}
          />
          <WizardHTMLEditor
            name="additional.economic"
            value={additionalInformation?.economic}
            label="Additional social and economic information"
            tooltip="The EPD may also include other relevant social and economic information as additional and voluntary information. This may be product information or a description of an organisation's overall work on social or economic sustainability, such as activities related to supply chain management or social responsibility."
            tooltipPosition="right"
            onChanged={onChangeEpdAdditionalInformation}
            stripPastedStyles={true}
            error={errors?.economic}
          />
          {isPcrV2 && (
            <WizardHTMLEditor
              name="additional.abbreviations"
              value={additionalInformation?.abbreviations}
              label="Abbreviations"
              tooltip='Indicate "Not applicable" if not relevant for your EPD.'
              tooltipPosition="right"
              onChanged={onChangeEpdAdditionalInformation}
              stripPastedStyles={true}
              required
            />
          )}
          {!isPcrV2 && (
            <>
              <WizardHTMLEditor
                name="additional.conversion"
                value={additionalInformation?.conversion}
                label="Conversion factors"
                tooltip="Conversion factors may be included in an EPD for the purposes of converting the declared results of a product group to results for specific products within the group, or converting the declared results to results for another declared/functional unit. Conversion factors can, however, not be included for the purpose of converting the declared results into results for products not covered by the EPD."
                tooltipPosition="right"
                onChanged={onChangeEpdAdditionalInformation}
                stripPastedStyles={true}
              />
              <WizardHTMLEditor
                name="additional.dangerous"
                value={additionalInformation?.dangerous}
                label="Dangerous substances to indoor air, soil, and water during the use stage"
                tooltip="Emissions to indoor air, soil, and water according to the horizontal standards on measurement of release of regulated dangerous substances from construction products using harmonised testing methods according to the provisions of the respective Technical Committees for European product standards, when available."
                tooltipPosition="right"
                onChanged={onChangeEpdAdditionalInformation}
                stripPastedStyles={true}
              />
            </>
          )}
        </FieldPanelFullWidth>
      )}
    </>
  );
};

export default AdditionalInformationTab;
